import { useLocation, useParams } from 'react-router-dom';
import { useMemo } from 'react';

export interface EmoiRouteContext {
  page: string;
  entityIds: Record<string, string>;
}

const PATTERNS: Array<{
  match: RegExp;
  page: string;
  capture: (m: RegExpMatchArray) => Record<string, string>;
}> = [
  {
    match: /\/committees\/([0-9a-f-]{36})/i,
    page: 'committee_detail',
    capture: (m) => ({ committee_id: m[1] }),
  },
  {
    match: /\/committees(?:\/?$|\?)/i,
    page: 'committees_list',
    capture: () => ({}),
  },
  {
    match: /\/opportunities\/([0-9a-f-]{36})\/solutions\/([0-9a-f-]{36})/i,
    page: 'solution_detail',
    capture: (m) => ({ opportunity_id: m[1], solution_id: m[2] }),
  },
  {
    match: /\/opportunities\/([0-9a-f-]{36})/i,
    page: 'opportunity_detail',
    capture: (m) => ({ opportunity_id: m[1] }),
  },
  {
    match: /\/opportunities(?:\/?$|\?)/i,
    page: 'opportunities_list',
    capture: () => ({}),
  },
  {
    match: /\/market-insights\/([0-9a-f-]{36})/i,
    page: 'market_insight_detail',
    capture: (m) => ({ market_insight_id: m[1] }),
  },
  {
    match: /\/market-insights(?:\/?$|\?)/i,
    page: 'market_insights_list',
    capture: () => ({}),
  },
  {
    match: /\/projects\/([0-9a-f-]{36})/i,
    page: 'project_detail',
    capture: (m) => ({ project_id: m[1] }),
  },
];

/**
 * Derive the page + entity ids visible from the URL so emoi can resolve
 * "this committee" / "this opportunity" without asking. useParams alone
 * doesn't help because the bubble is mounted at the app root, outside
 * any specific route's params.
 */
export function useRouteContext(): EmoiRouteContext {
  const location = useLocation();
  const params = useParams();

  return useMemo(() => {
    const path = location.pathname;
    for (const p of PATTERNS) {
      const m = path.match(p.match);
      if (m) {
        return { page: p.page, entityIds: { ...p.capture(m), ...prunedParams(params) } };
      }
    }
    return { page: 'unknown', entityIds: prunedParams(params) };
  }, [location.pathname, params]);
}

function prunedParams(p: Readonly<Record<string, string | undefined>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) {
    if (v && /^[0-9a-f-]{36}$/i.test(v)) out[k] = v;
  }
  return out;
}
